import { pantryColors, pantryTypography } from '@pantry/ui'
import React, { useRef } from 'react'

const p = pantryColors
const F = pantryTypography.fontFamily

export interface CouponDeleteConfirmDialogProps {
  open: boolean
  // The user-facing identifier for the coupon being deleted (e.g. the code
  // `SOURDOUGH5` or a display name). Embedded in the warning message.
  couponLabel: string
  // While the underlying delete mutation is in flight the buttons go disabled
  // and the primary button swaps to a loading label.
  isPending?: boolean
  onCancel: () => void
  onConfirm: () => void
}

// Destructive-confirm dialog used by the Coupon edit modal. Mirrors the
// `User/components/UserDrawer/ConfirmDeleteDialog` visual pattern so the
// destructive flows feel consistent across the Pantry module — backdrop +
// centered card + red-icon header + footer with cancel/destructive actions.
//
// Sits at z-index 1300 so it lands above the Coupon Modal (which uses
// `@pantry/ui` Modal at ~1200) without being covered by the underlying form.
export const CouponDeleteConfirmDialog: React.FC<
  CouponDeleteConfirmDialogProps
> = ({ open, couponLabel, isPending = false, onCancel, onConfirm }) => {
  const overlayRef = useRef<HTMLDivElement>(null)

  if (!open) return null

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isPending) return
    if (e.target === overlayRef.current) onCancel()
  }

  const cancelBtnStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 16px',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 500,
    cursor: isPending ? 'not-allowed' : 'pointer',
    border: `1px solid ${p.gray[300]}`,
    background: '#fff',
    color: p.gray[700],
    fontFamily: F,
    transition: 'all .15s',
    opacity: isPending ? 0.6 : 1,
  }

  const confirmBtnStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 16px',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 500,
    cursor: isPending ? 'not-allowed' : 'pointer',
    border: 'none',
    background: '#DC2626',
    color: '#fff',
    fontFamily: F,
    transition: 'all .15s',
    opacity: isPending ? 0.7 : 1,
  }

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="coupon-delete-confirm-title"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.35)',
        zIndex: 1300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 10,
          width: 440,
          maxWidth: '90vw',
          boxShadow: '0 20px 60px rgba(0,0,0,.18)',
          fontFamily: F,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '20px 24px 0',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: '#FEF2F2',
              color: '#DC2626',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </span>
          <span
            id="coupon-delete-confirm-title"
            style={{
              fontSize: 16,
              fontWeight: 700,
              color: p.gray[800],
              fontFamily: F,
            }}
          >
            Delete Coupon?
          </span>
        </div>

        <div
          style={{
            padding: '12px 24px 20px 72px',
            fontSize: 13,
            color: p.gray[600],
            lineHeight: 1.5,
            fontFamily: F,
            wordBreak: 'break-word',
          }}
        >
          Are you sure you want to delete{' '}
          <strong style={{ color: p.gray[800] }}>{couponLabel}</strong>? Members
          will no longer be able to redeem it. This action cannot be undone.
        </div>

        <div
          style={{
            padding: '12px 24px 20px',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            borderTop: `1px solid ${p.gray[100]}`,
            background: p.gray[50],
          }}
        >
          <button
            type="button"
            onClick={onCancel}
            disabled={isPending}
            style={cancelBtnStyle}
            onMouseEnter={(e) => {
              if (!isPending) e.currentTarget.style.background = p.gray[100]
            }}
            onMouseLeave={(e) => {
              if (!isPending) e.currentTarget.style.background = '#fff'
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isPending}
            style={confirmBtnStyle}
            onMouseEnter={(e) => {
              if (!isPending) e.currentTarget.style.background = '#B91C1C'
            }}
            onMouseLeave={(e) => {
              if (!isPending) e.currentTarget.style.background = '#DC2626'
            }}
          >
            {isPending ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  )
}
